import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Trash2, UserPlus, Eye, EyeOff } from "lucide-react";

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", is_admin: false });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setForm({ email: "", password: "", name: "", is_admin: false });
      setShowNew(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create user");
    } finally {
      setSaving(false);
    }
  };

  const toggleAdmin = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { is_admin: !u.is_admin });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update user");
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This also removes their profiles/watchlist.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete user");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-32 pb-24" data-testid="users-page">
      <div className="px-6 md:px-12 max-w-4xl mx-auto">
        <span className="text-xs uppercase tracking-[0.3em] text-[#D9381E]">Admin</span>
        <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter text-white mt-3">Users</h1>

        <button
          onClick={() => setShowNew(!showNew)}
          className="mt-8 flex items-center gap-2 bg-[#D9381E] hover:bg-[#ED4B32] text-white px-5 py-2 text-xs uppercase tracking-[0.2em]"
          data-testid="new-user-button"
        >
          <UserPlus size={14} strokeWidth={1.5} /> {showNew ? "Cancel" : "New User"}
        </button>

        {showNew && (
          <form onSubmit={createUser} className="mt-6 border border-[#222] p-6 space-y-4" data-testid="new-user-form">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">Name</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                className="mt-2 w-full bg-[#0F0F0F] border border-[#222] focus:border-[#D9381E] focus:outline-none text-white px-4 py-3" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">Email</span>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required
                className="mt-2 w-full bg-[#0F0F0F] border border-[#222] focus:border-[#D9381E] focus:outline-none text-white px-4 py-3" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">Password (min 8 chars)</span>
              <div className="relative mt-2">
                <input type={showPw ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required
                  className="w-full bg-[#0F0F0F] border border-[#222] focus:border-[#D9381E] focus:outline-none text-white px-4 py-3 pr-12" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A8A] hover:text-white">
                  {showPw ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                </button>
              </div>
            </label>
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={form.is_admin} onChange={(e) => setForm({ ...form, is_admin: e.target.checked })} />
              <span className="text-sm text-[#8A8A8A]">Admin access</span>
            </label>
            <button type="submit" disabled={saving} className="bg-[#D9381E] hover:bg-[#ED4B32] disabled:opacity-60 text-white px-8 py-3 text-sm uppercase tracking-[0.2em]">
              {saving ? "Creating…" : "Create User"}
            </button>
          </form>
        )}

        <div className="mt-10 border border-[#222]">
          <div className="grid grid-cols-12 px-5 py-3 border-b border-[#222] text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">
            <span className="col-span-4">Name</span>
            <span className="col-span-4">Email</span>
            <span className="col-span-2">Role</span>
            <span className="col-span-2 text-right">Actions</span>
          </div>
          {users.map((u) => (
            <div key={u.id} className="grid grid-cols-12 items-center px-5 py-4 border-b border-[#222] last:border-b-0 hover:bg-[#0F0F0F] transition-colors" data-testid={`user-row-${u.id}`}>
              <span className="col-span-4 text-white truncate">{u.name}</span>
              <span className="col-span-4 text-[#8A8A8A] text-sm truncate">{u.email}</span>
              <div className="col-span-2">
                <button
                  onClick={() => toggleAdmin(u)}
                  disabled={u.id === me?.id && u.is_admin}
                  title={u.id === me?.id && u.is_admin ? "Can't remove your own admin access" : "Toggle role"}
                  className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border disabled:opacity-40 disabled:cursor-not-allowed ${u.is_admin ? "border-[#D9381E] text-[#D9381E]" : "border-[#222] text-[#8A8A8A] hover:border-white hover:text-white"}`}
                  data-testid={`toggle-admin-${u.id}`}
                >
                  {u.is_admin ? "Admin" : "User"}
                </button>
              </div>
              <div className="col-span-2 flex justify-end">
                <button
                  onClick={() => removeUser(u)}
                  disabled={u.id === me?.id}
                  title={u.id === me?.id ? "Can't delete your own account" : "Delete"}
                  className="text-[#8A8A8A] hover:text-[#fca5a5] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  data-testid={`delete-user-${u.id}`}
                >
                  <Trash2 size={16} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
